import { POST } from "../../../../service/axiosMethods";
import { ELASTIC_URL } from "../../../../utils/apiConstant";

export function getDetachedCellRanges(gridRef) {
    const api = gridRef.current.api;
    const ranges = api.getCellRanges();

    if (!Array.isArray(ranges) || ranges.length === 0) return [];

    return ranges.map(range => {
        const startNode = api.getDisplayedRowAtIndex(range.startRow.rowIndex);
        const endNode   = api.getDisplayedRowAtIndex(range.endRow.rowIndex);

        return {
            startRow :{
                rowIndex : startNode?.sourceRowIndex ?? null,
            },
            endRow :{
                rowIndex : endNode?.sourceRowIndex ?? null,
            },
            // brand new array (no references)
            columns: range.columns,
            startColumn : range.startColumn
        };
    });
}

export const getDetachedFocusedCell = (gridRef) => {
    const api = gridRef.current?.api;
    if (!api) return null;

    const focused = api.getFocusedCell();
    if (!focused) return null;

    const displayedNode = api.getDisplayedRowAtIndex(focused.rowIndex);

    return {
        rowIndex: displayedNode?.sourceRowIndex ?? null,
        column: focused.column ?? null,     
        rowPinned: focused.rowPinned ?? null
    };
};

export const getDetachedSelectedRows = (gridRef) => {
    const api = gridRef.current?.api;
    if (!api) return [];

    const nodes = api.getSelectedNodes();

    return nodes.map(node => ({
        rowId: node.id,
        rowIndex: node.sourceRowIndex ?? null,
        data: { ...node.data }
    }));
};

export const getDetachedRowAtIndex = (gridRef, cellIndex) => {
    const api = gridRef.current?.api;
    if (!api) return null;

    const node = api.getDisplayedRowAtIndex(cellIndex);
    if (!node) return null;

    return {
        rowId: node.id,
        rowIndex: node.sourceRowIndex ?? null,
        data: { ...node.data }
    };
};


export async function updateColumnsForWrapInDB({
    sheetId,
    sheetIdentifier,
    selectedColumns,
    columnDefs,
    metaData = "column-wrap-update"
  }) {
    try {
      if (!selectedColumns?.length) return;
  
      // map only changed columns
      const columnsPayload = columnDefs
        .filter(col => selectedColumns.includes(col.colId))
        .map(col => ({
          columnElasticid: col.columnElasticid, // ⭐ required by backend
          updatedColumn: col
        }));
  
      if (!columnsPayload.length) return;
  
      await POST(`${ELASTIC_URL}/sheet/updateColumns`, {
          id: sheetId,
          sheet_identifier: sheetIdentifier,
          columns: columnsPayload,
          metaData
      });
  
    } catch (err) {
      console.error('Failed to update columns', err);
    }
  }


export function toggleColumnWrap({
  gridRef,
  selectedColumns,
  setSelectedColumns,
  setColumnDefs,
  currentSelectedSheet,
  setUndoStack,
  setRedoStack
}) {
  if (!selectedColumns?.length) return false;

  const api = gridRef.current.api;
  const selectedSet = new Set(selectedColumns);

  // setSelectedColumns([])

  setTimeout(() => {
    const allRows = api.getModel().rootNode.allLeafChildren;
    const baseDefs = api.getColumnDefs() || [];

    const previousRows = [];
    const cellRows = [];

    const processedRowIds = new Set(); // prevent duplicate pushes

    const nextWrapStateMap = {};

    // =========================
    // 1️⃣ Normal Columns
    // =========================
    baseDefs.forEach(col => {
      if (!selectedSet.has(col.colId)) return;
      nextWrapStateMap[col.colId] = !col.wrapText;
    });

    // =========================
    // 2️⃣ Auto Column
    // =========================
    if (selectedSet.has("ag-Grid-AutoColumn")) {
      const autoCol = api.getColumn("ag-Grid-AutoColumn");
      if (autoCol) {
        const def = autoCol.getColDef();
        nextWrapStateMap["ag-Grid-AutoColumn"] = !def.wrapText;
      }
    }

    // =========================
    // 3️⃣ HANDLE UNWRAP (remove cell overrides)
    // =========================
    Object.entries(nextWrapStateMap).forEach(([colId, nextState]) => {
      if (nextState !== true) return;

      const column = api.getColumn(colId);
      if (!column) return;

      const field = column.getColDef().field;
      const styleKey = field + "_es";

      allRows.forEach(node => {
        const row = node.data;
        if (!row) return;

        const cellStyle = row?.[styleKey]?.cellStyle;

        if (cellStyle && cellStyle.wrap) {
          const rowId = row.id;

          if (!processedRowIds.has(rowId)) {
            previousRows.push(JSON.parse(JSON.stringify(row)));
            processedRowIds.add(rowId);
          }

          const updatedCellStyle = { ...cellStyle };
          delete updatedCellStyle.wrap;

          row[styleKey] = {
            ...row[styleKey],
            cellStyle: updatedCellStyle
          };

          cellRows.push(row);
        }
      });
    });

    if (cellRows.length) {
      api.applyTransaction({ update: cellRows });
    }


    // =========================
    // 4️⃣ Apply normal columnDefs
    // =========================
    const newColumnDefs = baseDefs.map(col => {
      if (!selectedSet.has(col.colId)) return { ...col };

      return {
        ...col,
        wrapText: nextWrapStateMap[col.colId]
      };
    });

    setColumnDefs({ colDefs: newColumnDefs });

    // =========================
    // 5️⃣ Apply Auto Column wrap
    // =========================
    if (selectedSet.has("ag-Grid-AutoColumn")) {
      const autoCol = api.getColumn("ag-Grid-AutoColumn");
      if (autoCol) {
        const def = autoCol.getColDef();
        def.wrapText = nextWrapStateMap["ag-Grid-AutoColumn"];

        setColumnDefs({
          autoColumnGroupDefs: def
        });
      }
    }

    api.resetRowHeights();

    setUndoStack([
      {
        type: "column",
        action: "wrap-toggle",
        selectedColumns: [...selectedColumns],
        nextWrapStateMap,
        previousRows,
        cellRows,
        currentSelectedSheet
      }
    ]);

    // =========================
    // Prepare DB Payload
    // =========================
    const columnsPayload = [];

    selectedColumns.forEach(colId => {

      // 1️⃣ Normal columns
      const colDef = newColumnDefs.find(c => c.colId === colId);
      if (colDef && colDef.columnElasticid) {
        columnsPayload.push({
          columnElasticid: colDef.columnElasticid,
          updatedColumn: colDef
        });
        return;
      }

      // 2️⃣ Auto Column
      if (colId === "ag-Grid-AutoColumn") {
        const autoCol = api.getColumn("ag-Grid-AutoColumn");
        if (autoCol) {
          const autoDef = autoCol.getColDef();

          columnsPayload.push({
            columnElasticid: autoDef.columnElasticid,
            updatedColumn: autoDef
          });
        }
      }
    });

    if (columnsPayload.length) {
      updateColumnsForWrapInDB({
        sheetId: currentSelectedSheet.id,
        sheetIdentifier: currentSelectedSheet.sheet_identifier,
        selectedColumns,
        columnDefs: columnsPayload.map(c => c.updatedColumn),
        metaData: "wrap-toggle"
      });
    }
    api.clearRangeSelection();
    api.deselectAll();
  }, 500);
}

  export function toggleCellWrap({
    gridRef,
    applyStyle
  }) {
    const api = gridRef.current.api;
  
    const selectedRows = api.getSelectedNodes();
    const selectedRanges = getDetachedCellRanges(gridRef);
  
    const allRows = api.getModel().rootNode.allLeafChildren;
  
    let allWrapped = true;
  
    // =========================
    // CASE 1: Row Selection
    // =========================
    if (selectedRows?.length > 0) {
  
      const allColumns = api.getAllGridColumns()
        .filter(col => col.getColId() !== "#");
  
      selectedRows.forEach(node => {
        const row = node.data;
  
        allColumns.forEach(column => {
          const styleKey = column.getColDef().field + "_es";
  
          const isWrapped =
            row?.[styleKey]?.cellStyle?.wrap === "true";
  
          if (!isWrapped) {
            allWrapped = false;
          }
        });
      });
  
    }
  
    // =========================
    // CASE 2: Cell Range Selection
    // =========================
    else if (selectedRanges?.length > 0) {
  
      selectedRanges.forEach(range => {
        const { startRow, endRow, columns } = range;
  
        const start = Math.min(startRow.rowIndex, endRow.rowIndex);
        const end = Math.max(startRow.rowIndex, endRow.rowIndex);
  
        for (let rowIndex = start; rowIndex <= end; rowIndex++) {
          const row = allRows[rowIndex]?.data;
  
          for (const column of columns) {
            if (column.getColId() === "#") continue;
  
            const styleKey = column.getColDef().field + "_es";
  
            const isWrapped =
              row?.[styleKey]?.cellStyle?.wrap === "true";
  
            if (!isWrapped) {
              allWrapped = false;
              return;
            }
          }
        }
      });
  
    } else {
      return; // nothing selected
    }
  
    // =========================
    // Excel-style toggle
    // =========================
    const nextValue = allWrapped ? "false" : "true";
  
    applyStyle("wrap", nextValue);
  
    api.resetRowHeights();
  }